export const GRID_SIZE = 10;

export function snap(value, disabled = false) {
  if (disabled) return value;
  const snapped = Math.round(value / GRID_SIZE) * GRID_SIZE;
  return Object.is(snapped, -0) ? 0 : snapped;
}

const SIDES = ['left', 'right', 'top', 'bottom'];
const clamp = value => Math.max(0, Math.min(1, value));
const center = node => ({ x: node.x + node.w / 2, y: node.y + node.h / 2 });
const directions = { left: { x: -1, y: 0 }, right: { x: 1, y: 0 }, top: { x: 0, y: -1 }, bottom: { x: 0, y: 1 } };

export function anchorPoint(node, anchor = {}) {
  const side = SIDES.includes(anchor?.side) ? anchor.side : 'right';
  const offset = Number.isFinite(anchor?.offset) ? clamp(anchor.offset) : .5;
  if (side === 'left') return { x: node.x, y: node.y + node.h * offset };
  if (side === 'right') return { x: node.x + node.w, y: node.y + node.h * offset };
  if (side === 'top') return { x: node.x + node.w * offset, y: node.y };
  return { x: node.x + node.w * offset, y: node.y + node.h };
}

export function anchorAt(node, point) {
  const candidates = SIDES.map(side => {
    const offset = side === 'left' || side === 'right' ? clamp((point.y - node.y) / node.h) : clamp((point.x - node.x) / node.w);
    const anchor = { side, offset }, position = anchorPoint(node, anchor);
    return { anchor, distance: Math.hypot(point.x - position.x, point.y - position.y) };
  });
  candidates.sort((a, b) => a.distance - b.distance);
  return candidates[0].anchor;
}

function compact(points) {
  return points.filter((point, index) => index === 0 || point.x !== points[index - 1].x || point.y !== points[index - 1].y);
}

function pathFor(points) {
  return points.map((point, index) => {
    if (index === 0) return `M${point.x},${point.y}`;
    const previous = points[index - 1];
    if (point.y === previous.y) return `H${point.x}`;
    if (point.x === previous.x) return `V${point.y}`;
    return `L${point.x},${point.y}`;
  }).join(' ');
}

function midpoint(points) {
  const lengths = points.slice(1).map((point, index) => Math.hypot(point.x - points[index].x, point.y - points[index].y));
  let remaining = lengths.reduce((sum, length) => sum + length, 0) / 2;
  for (let index = 0; index < lengths.length; index++) {
    const length = lengths[index];
    if (remaining <= length && length > 0) {
      const ratio = remaining / length;
      return { x: points[index].x + (points[index + 1].x - points[index].x) * ratio, y: points[index].y + (points[index + 1].y - points[index].y) * ratio };
    }
    remaining -= length;
  }
  return points[0];
}

function defaultAnchors(source, target) {
  const a = center(source), b = center(target);
  if (Math.abs(a.x - b.x) > Math.abs(a.y - b.y) * 1.2) {
    return { source: { side: b.x > a.x ? 'right' : 'left', offset: .5 }, target: { side: b.x > a.x ? 'left' : 'right', offset: .5 }, horizontal: true };
  }
  return { source: { side: b.y > a.y ? 'bottom' : 'top', offset: .5 }, target: { side: b.y > a.y ? 'top' : 'bottom', offset: .5 }, horizontal: false };
}

function resolveAnchor(anchor, fallback) {
  return { side: SIDES.includes(anchor?.side) ? anchor.side : fallback.side, offset: Number.isFinite(anchor?.offset) ? clamp(anchor.offset) : fallback.offset };
}

export function edgeGeometry(edge, state) {
  const source = state.nodes.find(node => node.id === edge.source), target = state.nodes.find(node => node.id === edge.target);
  if (!source || !target) return null;
  const defaults = defaultAnchors(source, target);
  const sourceAnchor = resolveAnchor(edge.sourceAnchor, defaults.source), targetAnchor = resolveAnchor(edge.targetAnchor, defaults.target);
  const start = anchorPoint(source, sourceAnchor), end = anchorPoint(target, targetAnchor);
  let points;
  if (Array.isArray(edge.waypoints)) {
    // An explicitly empty array means a direct, manually controlled connection.
    points = [start, ...edge.waypoints.map(point => ({ x: point.x, y: point.y })), end];
  } else if (edge.sourceAnchor != null || edge.targetAnchor != null) {
    const sourceDirection = directions[sourceAnchor.side], targetDirection = directions[targetAnchor.side];
    const a = { x: start.x + sourceDirection.x * 20, y: start.y + sourceDirection.y * 20 };
    const b = { x: end.x + targetDirection.x * 20, y: end.y + targetDirection.y * 20 };
    if (sourceDirection.x && targetDirection.x) {
      const middle = (a.x + b.x) / 2;
      points = [start, a, { x: middle, y: a.y }, { x: middle, y: b.y }, b, end];
    } else if (sourceDirection.y && targetDirection.y) {
      const middle = (a.y + b.y) / 2;
      points = [start, a, { x: a.x, y: middle }, { x: b.x, y: middle }, b, end];
    } else {
      const corner = sourceDirection.x ? { x: b.x, y: a.y } : { x: a.x, y: b.y };
      points = [start, a, corner, b, end];
    }
  } else if (defaults.horizontal) {
    const blocked = state.nodes.some(node => node.type === 'service' && node.id !== source.id && node.id !== target.id && node.x < Math.max(start.x, end.x) && node.x + node.w > Math.min(start.x, end.x) && start.y > node.y && start.y < node.y + node.h);
    if (blocked) {
      const direction = end.x > start.x ? 1 : -1, y = Math.max(source.y + source.h, target.y + target.h) + 34;
      points = [start, { x: start.x + direction * 20, y: start.y }, { x: start.x + direction * 20, y }, { x: target.x + target.w / 2, y }, { x: target.x + target.w / 2, y: target.y + target.h }];
    } else {
      const middle = (start.x + end.x) / 2;
      points = [start, { x: middle, y: start.y }, { x: middle, y: end.y }, end];
    }
  } else {
    const middle = (start.y + end.y) / 2;
    points = [start, { x: start.x, y: middle }, { x: end.x, y: middle }, end];
  }
  points = compact(points);
  const label = midpoint(points);
  return { path: pathFor(points), points, x: label.x + (edge.labelOffset?.x || 0), y: label.y + (edge.labelOffset?.y || 0) };
}
